import { useTranslation } from 'react-i18next';
import type {
  DynamicPricingCalendarApartment,
  DynamicPricingCalendarDayDetailResponse,
} from '@/shared/api';
import { price as money, signed } from './calendarDisplay';
import { DynamicPricingFact as Fact } from './DynamicPricingFact';

export function DynamicPricingExposureSection({
  apartment,
  detail,
  loading,
  error,
}: {
  apartment: DynamicPricingCalendarApartment;
  detail?: DynamicPricingCalendarDayDetailResponse;
  loading: boolean;
  error: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section className="border-line space-y-3 border-t pt-4 text-sm">
      <h3 className="font-serif text-lg">
        {t('admin.dynamicPricing.calendar.details.exposureProvenance')}
      </h3>
      {loading ? (
        <p className="text-ink-soft text-sm">
          {t('admin.dynamicPricing.calendar.details.exposureLoading')}
        </p>
      ) : error ? (
        <p className="text-sealwax text-sm">
          {t('admin.dynamicPricing.calendar.details.exposureError')}
        </p>
      ) : detail?.baseExposure ? (
        <div className="space-y-2">
          <Fact
            label={t('admin.dynamicPricing.calendar.details.baseReadback')}
            value={t('admin.dynamicPricing.calendar.details.baseReadbackValue', {
              rate: money(detail.baseExposure.rate, apartment.currency),
              minimumStay: detail.baseExposure.minimumStay ?? '—',
            })}
          />
          <Fact
            label={t('admin.dynamicPricing.calendar.details.confirmationLevel')}
            value={detail.baseExposure.confirmationLevel}
            mono
          />
          {detail.channelExposures.map((exposure) => (
            <div key={exposure.exposureId} className="bg-card-alt rounded-xl p-3">
              <strong className="block text-sm">
                {t('admin.dynamicPricing.calendar.details.channelModifier')}
              </strong>
              <span className="text-ink-soft block text-xs">
                {signed(exposure.modifierPercent ?? 0)} % →{' '}
                {money(exposure.channelRate, apartment.currency)}
              </span>
              <span className="text-ink-faint block truncate font-mono text-[10px]">
                {t('admin.dynamicPricing.calendar.details.basis', {
                  id: exposure.basisExposureId ?? '—',
                })}
              </span>
              <span className="text-ink-faint block font-mono text-[10px]">
                {exposure.confirmationLevel}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-ink-soft text-sm">
          {t('admin.dynamicPricing.calendar.details.noExposure')}
        </p>
      )}
    </section>
  );
}
